import { memo, useState } from "react";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import type {
  SubagentTimelineItem,
  SubagentConversationTurn,
} from "@/hooks/useChat";
import { MessageFrame } from "./MessageFrame";

const SubagentConversationLog = ({
  turns,
}: {
  turns: SubagentConversationTurn[];
}) => (
  <div className="border-t border-white/[0.04] px-3 py-2 space-y-2">
    {turns.map((turn, i) => (
      <div
        key={i}
        className={cn(
          "text-[13px] leading-relaxed",
          turn.role === "user" && "text-zinc-400",
          turn.role === "agent" && "text-zinc-300",
          turn.role === "system" && "text-red-400",
        )}
      >
        <span className="mr-1 text-[11px] font-medium uppercase text-zinc-600">
          {turn.role}:
        </span>
        {turn.text.slice(0, 500)}
        {turn.text.length > 500 ? "..." : ""}
      </div>
    ))}
  </div>
);

export const SubagentCard = memo(function SubagentCard({
  item,
}: {
  item: SubagentTimelineItem;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <MessageFrame side="bot" wide>
      <div className="overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.02]">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] transition-colors hover:bg-white/[0.03]"
        >
          <span
            className={cn(
              "h-1.5 w-1.5 rounded-full",
              item.status === "running" && "bg-emerald-400",
              item.status === "done" && "bg-zinc-500",
              item.status === "error" && "bg-red-400",
            )}
          />
          <span className="flex-1 truncate text-zinc-400">
            Sub-agent: {item.task.slice(0, 80)}
            {item.task.length > 80 ? "..." : ""}
          </span>
          <span className="flex max-w-[38%] items-center gap-1 truncate text-[11px] text-zinc-600">
            {item.status === "running" && (
              <Loader2 className="h-3 w-3 shrink-0 animate-spin text-[#7678E3]" />
            )}
            <span className="truncate">
              {item.status === "running"
                ? item.workingStatus || "running"
                : item.status}
            </span>
          </span>
        </button>
        {expanded && item.conversationTurns.length > 0 && (
          <SubagentConversationLog turns={item.conversationTurns} />
        )}
      </div>
    </MessageFrame>
  );
});
